// SEO utilities for Mailsball
// Keep MAIN_DOMAIN in sync with backend/config/emailDnsConstants.js
export const MAIN_DOMAIN = "mailsball.com";
export const SITE_URL = `https://${MAIN_DOMAIN}`;

export const DEFAULT_TITLE = "Mailsball - Email & SMS Marketing Platform";
export const DEFAULT_DESCRIPTION =
  "Professional email & SMS marketing platform for growing businesses. Send email and SMS campaigns, automate sequences, and increase conversions. Join 1,000+ businesses growing with Mailsball.";
export const EMAIL_KEYWORDS =
  "email marketing, email campaigns, email automation, email marketing platform, mailchimp alternative, brevo alternative, email marketing software, bulk email service, email list building, email templates";
export const SMS_KEYWORDS =
  "SMS marketing, SMS campaigns, SMS automation, SMS marketing platform, SMS marketing software, bulk SMS service, SMS blast, text message marketing";
export const DEFAULT_KEYWORDS = `${EMAIL_KEYWORDS}, ${SMS_KEYWORDS}`;
export const DEFAULT_IMAGE = `${SITE_URL}/images/Loogoo.png`;

// Page-specific SEO data
export const PAGE_SEO = {
  home: {
    title: "Email & SMS Marketing Platform | Mailsball",
    description: DEFAULT_DESCRIPTION,
    keywords: DEFAULT_KEYWORDS,
    canonical: SITE_URL,
    ogTitle: "Powerful Email & SMS Marketing That Actually Converts | Mailsball",
    structured: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Mailsball",
      url: SITE_URL,
      description:
        "Professional email & SMS marketing platform for growing businesses",
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  },
  about: {
    title: "About Us - Email & SMS Marketing Experts | Mailsball",
    description:
      "Learn about Mailsball's mission to revolutionize email and SMS marketing. Meet our expert team and discover why 1,000+ businesses trust us with their campaigns.",
    keywords:
      `about mailsball, email marketing company, email marketing team, mailsball story, email marketing experts, ${SMS_KEYWORDS}`,
    canonical: `${SITE_URL}/about`,
    ogTitle: "About Us - Email & SMS Marketing Experts | Mailsball",
    structured: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Mailsball",
      url: SITE_URL,
      logo: `${SITE_URL}/images/logo.svg`,
      description:
        "Professional email & SMS marketing platform for growing businesses",
      founder: {
        "@type": "Person",
        name: "Aleksandr Khrustalov",
      },
      numberOfEmployees: "10-50",
      foundingDate: "2018",
      sameAs: [
        "https://twitter.com/mailsball",
        "https://linkedin.com/company/mailsball",
      ],
    },
  },
  pricing: {
    title: "Email & SMS Marketing Pricing Plans | Mailsball",
    description:
      "Choose the perfect email and SMS marketing plan for your business. Transparent pricing for email campaigns and SMS credits — no hidden fees. Start free and scale as you grow.",
    keywords:
      `email marketing pricing, mailsball plans, email marketing cost, affordable email marketing, SMS pricing, SMS credits, ${SMS_KEYWORDS}`,
    canonical: `${SITE_URL}/pricing`,
    ogTitle: "Affordable Email & SMS Marketing Plans | Mailsball",
    structured: {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Email & SMS Marketing Service",
      provider: {
        "@type": "Organization",
        name: "Mailsball",
        url: SITE_URL,
      },
      description:
        "Professional email & SMS marketing platform with flexible pricing plans",
      serviceType: "Email & SMS Marketing Platform",
    },
  },
  login: {
    title: "Login to Your Email & SMS Marketing Dashboard | Mailsball",
    description:
      "Access your Mailsball dashboard. Manage email and SMS campaigns, view analytics, and grow your business.",
    keywords:
      `mailsball login, email marketing dashboard, SMS dashboard, user login, ${SMS_KEYWORDS}`,
    canonical: `${SITE_URL}/login`,
    ogTitle: "Login to Mailsball Email & SMS Marketing Platform",
    structured: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Login Page",
      description:
        "User login page for Mailsball email & SMS marketing platform",
      url: `${SITE_URL}/login`,
    },
  },
  register: {
    title: "Start Free Email & SMS Marketing Trial | Mailsball",
    description:
      "Create your free Mailsball account today. Start sending professional email and SMS campaigns in minutes with our powerful platform.",
    keywords:
      `email marketing signup, free email marketing trial, SMS marketing trial, create account mailsball, ${SMS_KEYWORDS}`,
    canonical: `${SITE_URL}/register`,
    ogTitle: "Start Your Free Email & SMS Marketing Trial | Mailsball",
    structured: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Registration Page",
      description:
        "Create a free account to start using Mailsball email & SMS marketing platform",
      url: `${SITE_URL}/register`,
    },
  },
  "privacy-policy": {
    title: "Privacy Policy | Mailsball",
    description:
      "Learn how Mailsball protects your privacy and data when using our email and SMS marketing platform. Our comprehensive privacy policy outlines our commitment to data security.",
    keywords:
      `mailsball privacy policy, data protection, privacy, GDPR compliance, SMS data protection, ${SMS_KEYWORDS}`,
    canonical: `${SITE_URL}/privacy-policy`,
    ogTitle: "Privacy Policy | Mailsball",
    structured: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Privacy Policy",
      description:
        "Privacy policy for Mailsball email & SMS marketing platform",
      url: `${SITE_URL}/privacy-policy`,
    },
  },
  "terms-of-service": {
    title: "Terms of Service | Mailsball",
    description:
      "Read Mailsball's terms of service. Understand your rights and responsibilities when using our email and SMS marketing platform.",
    keywords:
      `mailsball terms of service, terms and conditions, user agreement, SMS terms, ${SMS_KEYWORDS}`,
    canonical: `${SITE_URL}/terms-of-service`,
    ogTitle: "Terms of Service | Mailsball",
    structured: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Terms of Service",
      description:
        "Terms of service for Mailsball email & SMS marketing platform",
      url: `${SITE_URL}/terms-of-service`,
    },
  },
};

// Generate structured data script
export const generateStructuredData = (data) => {
  return {
    type: "application/ld+json",
    innerHTML: JSON.stringify(data),
  };
};

// Generate page title with brand
export const generateTitle = (pageTitle) => {
  if (!pageTitle || pageTitle === DEFAULT_TITLE) {
    return DEFAULT_TITLE;
  }
  return pageTitle;
};

// Generate canonical URL
export const generateCanonical = (path) => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
};

// Get SEO data for a specific page
export const getPageSEO = (pageName) => {
  return (
    PAGE_SEO[pageName] || {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      keywords: DEFAULT_KEYWORDS,
      canonical: SITE_URL,
      ogTitle: DEFAULT_TITLE,
    }
  );
};

// Generate Open Graph meta tags
export const generateOGTags = (seoData) => {
  return {
    "og:title": seoData.ogTitle || seoData.title,
    "og:description": seoData.description,
    "og:url": seoData.canonical,
    "og:type": "website",
    "og:image": DEFAULT_IMAGE,
    "og:site_name": "Mailsball",
  };
};

// Generate Twitter Card meta tags
export const generateTwitterTags = (seoData) => {
  return {
    "twitter:card": "summary_large_image",
    "twitter:title": seoData.ogTitle || seoData.title,
    "twitter:description": seoData.description,
    "twitter:image": DEFAULT_IMAGE,
    "twitter:site": "@mailsball",
    "twitter:creator": "@mailsball",
  };
};

// Sitemap generation utilities
export const SITEMAP_PAGES = [
  { url: "/", changefreq: "daily", priority: "1.0" },
  { url: "/about", changefreq: "monthly", priority: "0.8" },
  { url: "/pricing", changefreq: "weekly", priority: "0.9" },
  { url: "/login", changefreq: "monthly", priority: "0.6" },
  { url: "/register", changefreq: "monthly", priority: "0.7" },
  { url: "/privacy-policy", changefreq: "yearly", priority: "0.3" },
  { url: "/terms-of-service", changefreq: "yearly", priority: "0.3" },
];

export const generateSitemap = () => {
  const urlset = SITEMAP_PAGES.map(
    (page) => `
  <url>
    <loc>${SITE_URL}${page.url}</loc>
    <changefreq>${page.changefreq}</changefreq>
    <priority>${page.priority}</priority>
    <lastmod>${new Date().toISOString().split("T")[0]}</lastmod>
  </url>`,
  ).join("");

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:news="http://www.google.com/schemas/sitemap-news/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml" xmlns:image="http://www.google.com/schemas/sitemap-image/1.1" xmlns:video="http://www.google.com/schemas/sitemap-video/1.1">
${urlset}
</urlset>`;
};
